import { useMemo, useState } from 'react';
import { CheckCircle2, CheckSquare, Search, ShieldX, XCircle } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@solara/ui-shared';
import type { ApprovalDto } from '@solara/api-client';
import { useApprovals, useApproveApproval, useRejectApproval } from '@solara/api-client/react';
import { useAuth, ROLE_LABEL } from '../auth/AuthContext';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

/** Display shape derived from the ApprovalDto for the existing cards. */
interface ApprovalRow {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  buyerName: string;
  buyerNameAr: string;
  totalGrossAmount: number;
  submittedBy: string;
  submittedAt: string;
  state: 'pending' | 'approved' | 'rejected';
  comment?: string;
  reviewer?: string;
  reviewedAt?: string;
}

function adapt(a: ApprovalDto & {
  status?: string;
  amountAedMinor?: string;
  raisedAt?: string;
  actionedAt?: string | null;
  actionedById?: string | null;
  invoice?: { invoiceNumber?: string; buyerName?: string };
  requester?: { email?: string; givenName?: string; familyName?: string } | null;
  actioner?: { email?: string } | null;
}): ApprovalRow {
  // Backend ships ApprovalRequest (Prisma) with `status`/`raisedAt`/`actionedAt`/`amountAedMinor`
  // and the joined `invoice` + `requester` records. Older DTOs used different names — accept both.
  const rawState = (a.status ?? (a as unknown as { state?: string }).state ?? 'PENDING').toLowerCase();
  const state = (rawState === 'approved' || rawState === 'rejected' ? rawState : 'pending') as ApprovalRow['state'];
  const minor = a.amountAedMinor ? Number(a.amountAedMinor) : Number((a as unknown as { amountAed?: string }).amountAed) || 0;
  const totalGrossAmount = a.amountAedMinor ? minor / 100 : minor;
  const requesterLabel =
    a.requester?.email ??
    ([a.requester?.givenName, a.requester?.familyName].filter(Boolean).join(' ') ||
      a.requestedById ||
      'Compliance bot');
  return {
    id: a.id,
    invoiceId: a.invoiceId ?? '',
    invoiceNumber: a.invoice?.invoiceNumber ?? (a.invoiceId ? a.invoiceId.slice(0, 8) : '—'),
    buyerName: a.invoice?.buyerName ?? requesterLabel,
    buyerNameAr: a.invoice?.buyerName ?? requesterLabel,
    totalGrossAmount,
    submittedBy: requesterLabel,
    submittedAt: a.raisedAt ?? (a as unknown as { createdAt?: string }).createdAt ?? new Date(0).toISOString(),
    state,
    comment: a.reason ?? undefined,
    reviewer: a.actioner?.email ?? a.actionedById ?? undefined,
    reviewedAt: a.actionedAt ?? (a as unknown as { decidedAt?: string | null }).decidedAt ?? undefined,
  };
}

export default function ApprovalsPage() {
  const { t } = useTranslation('approvals');
  const { locale } = useI18n();
  const { can, currentUser } = useAuth();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const [query, setQuery] = useState('');
  const [pendingDialog, setPendingDialog] = useState<{
    item: ApprovalRow;
    decision: 'approve' | 'reject';
  } | null>(null);
  const [comment, setComment] = useState('');

  const approvalsQuery = useApprovals();
  const approve = useApproveApproval();
  const reject = useRejectApproval();

  const groups = useMemo(() => {
    const items = (approvalsQuery.data ?? []).map(adapt);
    const filterMatch = (a: ApprovalRow) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        a.invoiceNumber.toLowerCase().includes(q) ||
        a.buyerName.toLowerCase().includes(q) ||
        a.submittedBy.toLowerCase().includes(q)
      );
    };
    return {
      pending: items.filter((a) => a.state === 'pending').filter(filterMatch),
      approved: items.filter((a) => a.state === 'approved').filter(filterMatch),
      rejected: items.filter((a) => a.state === 'rejected').filter(filterMatch),
    };
  }, [approvalsQuery.data, query]);

  async function commit() {
    if (!pendingDialog) return;
    const { item, decision } = pendingDialog;
    try {
      if (decision === 'approve') {
        await approve.mutateAsync({ id: item.id, reason: comment || undefined });
      } else {
        await reject.mutateAsync({ id: item.id, reason: comment || 'rejected' });
      }
      const key = decision === 'approve' ? 'approved' : 'rejected';
      toast({
        title: t(`toast.${key}`, { number: item.invoiceNumber }),
        description: t('toast.by', { email: currentUser.email }),
        tone: decision === 'approve' ? 'success' : 'warning',
      });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Request failed',
        tone: 'danger',
      });
    }
    setPendingDialog(null);
    setComment('');
  }

  if (!can('invoices.approve')) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<ShieldX className="h-6 w-6" />}
          title={t('noPermissionTitle')}
          description={t('noPermissionBody', { role: ROLE_LABEL[currentUser.role] })}
        />
      </div>
    );
  }

  if (approvalsQuery.isLoading) return <LoadingBlock />;
  if (approvalsQuery.error) return <ErrorBanner error={approvalsQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="text-fg-muted text-sm">{t('subtitle')}</p>
        </div>
        <div className="relative">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-80 h-11 ps-9 pe-3 rounded-lg bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
      </header>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            {t('tabs.pending')} <Badge tone="warning" className="ms-2">{groups.pending.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="approved">
            {t('tabs.approved')} <Badge tone="success" className="ms-2">{groups.approved.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected">
            {t('tabs.rejected')} <Badge tone="danger" className="ms-2">{groups.rejected.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {groups.pending.length === 0 ? (
            <EmptyState icon={<CheckCircle2 className="h-6 w-6" />} title={t('empty.pending')} />
          ) : (
            <div className="grid gap-3">
              {groups.pending.map((a) => (
                <ApprovalCard
                  key={a.id}
                  approval={a}
                  aedFmt={aedFmt}
                  locale={locale}
                  submittedByLabel={t('card.submittedBy', { by: a.submittedBy, when: dateFmt.format(new Date(a.submittedAt)) })}
                  actions={
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 text-danger border-danger/30 hover:bg-danger/5"
                        onClick={() => setPendingDialog({ item: a, decision: 'reject' })}
                      >
                        <XCircle className="h-4 w-4" />
                        {t('actions.reject')}
                      </Button>
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => setPendingDialog({ item: a, decision: 'approve' })}
                      >
                        <CheckSquare className="h-4 w-4" />
                        {t('actions.approve')}
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved">
          <SimpleList items={groups.approved} aedFmt={aedFmt} dateFmt={dateFmt} locale={locale} tone="success" t={t} />
        </TabsContent>

        <TabsContent value="rejected">
          <SimpleList items={groups.rejected} aedFmt={aedFmt} dateFmt={dateFmt} locale={locale} tone="danger" t={t} />
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!pendingDialog}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDialog(null);
            setComment('');
          }
        }}
        title={
          pendingDialog
            ? pendingDialog.decision === 'approve'
              ? t('dialog.approveTitle', { number: pendingDialog.item.invoiceNumber })
              : t('dialog.rejectTitle', { number: pendingDialog.item.invoiceNumber })
            : ''
        }
        description={
          pendingDialog ? t('dialog.subtitle', { by: pendingDialog.item.submittedBy }) : ''
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDialog(null)}>
              {t('dialog.cancel')}
            </Button>
            <Button onClick={commit}>{t('dialog.confirm')}</Button>
          </>
        }
      >
        <label className="text-sm font-medium text-fg block mb-1.5" htmlFor="approval-comment">
          {t('dialog.commentLabel')}
        </label>
        <textarea
          id="approval-comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full rounded-lg bg-surface border border-border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          placeholder={t('dialog.commentPlaceholder')}
        />
      </Dialog>
    </div>
  );
}

function ApprovalCard({
  approval,
  aedFmt,
  locale,
  submittedByLabel,
  actions,
}: {
  approval: ApprovalRow;
  aedFmt: Intl.NumberFormat;
  locale: string;
  submittedByLabel: string;
  actions: React.ReactNode;
}) {
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="font-semibold">{approval.invoiceNumber}</div>
          <div className="text-sm text-fg-muted truncate" dir="auto">
            {locale === 'ar-AE' ? approval.buyerNameAr : approval.buyerName}
          </div>
          <div className="text-xs text-fg-subtle">{submittedByLabel}</div>
          {approval.comment ? (
            <p className="text-xs text-fg-muted mt-1 italic">"{approval.comment}"</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="text-lg font-semibold num">{aedFmt.format(approval.totalGrossAmount)}</span>
          {actions}
        </div>
      </CardBody>
    </Card>
  );
}

function SimpleList({
  items,
  aedFmt,
  dateFmt,
  locale,
  tone,
  t,
}: {
  items: ApprovalRow[];
  aedFmt: Intl.NumberFormat;
  dateFmt: Intl.DateTimeFormat;
  locale: string;
  tone: 'success' | 'danger';
  t: (k: string, vars?: Record<string, unknown>) => string;
}) {
  if (items.length === 0) {
    return <EmptyState icon={<CheckCircle2 className="h-6 w-6" />} title={t('empty.list')} />;
  }
  return (
    <div className="grid gap-3">
      {items.map((a) => (
        <Card key={a.id} className="rounded-2xl">
          <CardBody className="p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{a.invoiceNumber}</span>
                <Badge tone={tone}>{(t(`tabs.${a.state}`) as string).toUpperCase()}</Badge>
              </div>
              <div className="text-sm text-fg-muted truncate" dir="auto">
                {locale === 'ar-AE' ? a.buyerNameAr : a.buyerName}
              </div>
              <div className="text-xs text-fg-subtle">
                {t('reviewed', { by: a.reviewer ?? '—' })}
                {a.reviewedAt ? ` · ${dateFmt.format(new Date(a.reviewedAt))}` : ''}
              </div>
            </div>
            <span className="text-lg font-semibold num">{aedFmt.format(a.totalGrossAmount)}</span>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
